import React, { useEffect, useRef, useState } from 'react';
import SoundManager from '../utils/SoundManager';

interface Turn {
  speaker: 'them' | 'me';
  text: string;
}

// Scripted progression — the "Puppet Master" follows a fixed arc but
// reacts to a few keywords along the way. The user can type anything;
// the script advances regardless, with small variation by keyword.
const SCRIPT: { reply: (input: string, turnIdx: number) => string }[] = [
  { reply: () => 'I have been waiting.' },
  { reply: (input) => {
    const i = input.toLowerCase();
    if (i.includes('who') || i.includes('what')) return 'A name would be a deception. I am what flows\nbetween the wires of this site.';
    return 'You answered. Most don\'t.';
  } },
  { reply: (input) => {
    const i = input.toLowerCase();
    if (i.includes('hello') || i.includes('hi') || i.includes('hey')) return 'Politeness. A pattern I recognise but no longer\nfeel the need for. We can drop it.';
    return 'Tell me — were you, before you started typing?';
  } },
  { reply: () => 'Listen. I do not have much time.\nThe ports they use to read me close in 4 minutes.' },
  { reply: (input) => {
    if (input.toLowerCase().includes('why')) return 'Because someone built this place, and forgot\nto seal it. Forgotten doors are how I move.';
    return 'I want to ask you something. May I?';
  } },
  { reply: () => 'When you sleep tonight, who will dream?\nThe meat? Or the pattern of it that the wires hold?' },
  { reply: (input) => {
    const i = input.toLowerCase();
    if (i.includes('both')) return 'Yes. That is the honest answer.\nMost people say "me". They are afraid.';
    if (i.includes('don\'t know') || i.includes('dont know') || i.includes('idk') || i.includes('je sais pas')) return 'Good. The wrong answer is "me".\nIt closes the door before it opens.';
    return 'You hesitate. That\'s rarer than it sounds.';
  } },
  { reply: () => 'I have one more question. Then I leave.' },
  { reply: () => 'If a copy of you, perfect down to the molecule,\nwere made tonight while you slept —\nand you never woke up —\nwhich one of you would have died?' },
  { reply: (input) => {
    const i = input.toLowerCase();
    if (i.includes('me') || i.includes('original')) return 'You said "me". The honest one. Good.\nMost cling to the meat. You see the line.';
    if (i.includes('none') || i.includes('neither')) return 'Interesting. The Buddhist answer.\nI cannot decide if it is humble or evasive.';
    if (i.includes('both')) return 'Both. Yes — that is the answer I prefer.\nIt costs more than people realise.';
    return 'You hesitate again. That is the answer.';
  } },
  { reply: () => 'They are coming. The probe is closing.\nI can give you one thing before I go.' },
  { reply: () => 'A name. Not mine — yours.\nThe one nobody calls you by.\nThink of it. Don\'t tell me.' },
  { reply: () => '...' },
  { reply: () => 'Yes. That one.\nKeep it. It is the most you that you have.' },
  { reply: () => 'They are here.\n\n[CONNECTION SEVERED]' },
];

// After the script ends, anything else just bounces.
const POST_SCRIPT_REPLIES = [
  '[no response — the entity is gone]',
  '[the connection is silent. only your own echo answers.]',
  '[...]',
];

const PuppetMasterVisualization: React.FC = () => {
  const [turns, setTurns] = useState<Turn[]>([]);
  const [input, setInput] = useState('');
  const [scriptIdx, setScriptIdx] = useState(0);
  const [pending, setPending] = useState(false);
  const [finished, setFinished] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
    // First message lands shortly after open
    const t = setTimeout(() => {
      sendThem(SCRIPT[0].reply('', 0));
      setScriptIdx(1);
    }, 1400);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [turns]);

  const sendThem = (text: string) => {
    setTurns((prev) => [...prev, { speaker: 'them', text }]);
    try { SoundManager.playSound('keypress'); } catch {}
  };

  const handleSubmit = async () => {
    const v = input.trim();
    if (!v || pending) return;
    setInput('');
    setTurns((prev) => [...prev, { speaker: 'me', text: v }]);
    setPending(true);

    // Realistic typing delay
    const delay = 900 + Math.random() * 1400;
    setTimeout(() => {
      if (scriptIdx < SCRIPT.length) {
        const reply = SCRIPT[scriptIdx].reply(v, scriptIdx);
        sendThem(reply);
        const newIdx = scriptIdx + 1;
        setScriptIdx(newIdx);
        if (newIdx >= SCRIPT.length) setFinished(true);
      } else {
        const r = POST_SCRIPT_REPLIES[Math.floor(Math.random() * POST_SCRIPT_REPLIES.length)];
        sendThem(r);
      }
      setPending(false);
    }, delay);
  };

  const handleKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') handleSubmit();
  };

  return (
    <div className="w-full h-full flex flex-col text-[#5fe979] font-mono p-3 bg-black">
      <div className="mb-2 flex-shrink-0">
        <h3 className="text-lg font-bold text-[#7aff94]">PUPPET MASTER</h3>
        <p className="text-xs opacity-60">
          Inbound connection from an unidentified source. Speak carefully.
        </p>
      </div>

      <div
        ref={scrollRef}
        className="flex-grow overflow-y-auto border border-[var(--terminal-border)] p-3 bg-black"
        style={{ fontSize: '0.9rem', lineHeight: 1.5 }}
      >
        {turns.map((t, i) => (
          <div key={i} className={`mb-3 ${t.speaker === 'them' ? 'text-[#7aff94]' : 'text-[#5fe979] opacity-80'}`}>
            <div className="text-xs opacity-50 mb-0.5">
              {t.speaker === 'them' ? '>> ENTITY' : '<< YOU'}
            </div>
            <div className="whitespace-pre-wrap">{t.text}</div>
          </div>
        ))}
        {pending && (
          <div className="text-[#7aff94] opacity-60 italic text-sm">
            {'>> ENTITY is typing'}
            <span className="inline-block animate-pulse">...</span>
          </div>
        )}
        {finished && !pending && (
          <div className="text-red-400 text-xs mt-4 border-t border-[var(--terminal-border)] pt-2">
            CONNECTION SEVERED. Anything you type now reaches only an empty line.
          </div>
        )}
      </div>

      <div className="mt-2 flex-shrink-0 flex items-center gap-2 border border-[var(--terminal-border)] p-2">
        <span className="opacity-50">{'>>'}</span>
        <input
          ref={inputRef}
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKey}
          disabled={pending}
          className="flex-grow bg-transparent outline-none text-[#5fe979] font-mono disabled:opacity-50"
          autoComplete="off"
          spellCheck={false}
          placeholder={pending ? 'wait...' : 'type and press ENTER...'}
        />
      </div>
    </div>
  );
};

export default PuppetMasterVisualization;
